import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRightLeft, Users, Crown, AlertTriangle } from "lucide-react";

interface Team {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  lead_name: string | null;
}

interface SwitchTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string;
  currentTeamId: string;
  currentUserIsLead: boolean;
  onTeamSwitched?: () => void;
}

const SwitchTeamDialog: React.FC<SwitchTeamDialogProps> = ({
  open,
  onOpenChange,
  challengeId,
  currentTeamId,
  currentUserIsLead,
  onTeamSwitched,
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [confirmTeam, setConfirmTeam] = useState<Team | null>(null);
  const [currentTeamMemberCount, setCurrentTeamMemberCount] = useState(0);

  useEffect(() => {
    if (open) {
      fetchTeams();
    }
  }, [open, challengeId, currentTeamId]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      // Fetch all active teams for this challenge except current
      const { data: teamsData, error: teamsError } = await supabase
        .from("challenge_teams")
        .select(`
          id, name, description,
          challenge_team_members(user_id, role)
        `)
        .eq("challenge_id", challengeId)
        .neq("id", currentTeamId)
        .is("deleted_at", null);

      if (teamsError) throw teamsError;

      // Get current team member count
      const { count } = await supabase
        .from("challenge_team_members")
        .select("id", { count: "exact" })
        .eq("team_id", currentTeamId);
      
      setCurrentTeamMemberCount(count || 0);

      // Process teams to get member count and lead name
      const processedTeams: Team[] = await Promise.all(
        (teamsData || []).map(async (team) => {
          const members = team.challenge_team_members || [];
          const lead = members.find((m: any) => m.role === "lead");
          
          let leadName = null;
          if (lead) {
            const { data: profile } = await supabase
              .from("contributor_profiles")
              .select("display_name")
              .eq("id", lead.user_id)
              .single();
            leadName = profile?.display_name || "Unknown";
          }

          return {
            id: team.id,
            name: team.name,
            description: team.description,
            member_count: members.length,
            lead_name: leadName,
          };
        })
      );

      setTeams(processedTeams);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchTeam = async (team: Team) => {
    // If user is lead with other members, show warning
    if (currentUserIsLead && currentTeamMemberCount > 1) {
      setConfirmTeam(team);
      return;
    }
    
    await performSwitch(team);
  };

  const performSwitch = async (team: Team) => {
    setSwitching(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Remove from current team
      const { error: removeError } = await supabase
        .from("challenge_team_members")
        .delete()
        .eq("team_id", currentTeamId)
        .eq("user_id", user.id);

      if (removeError) throw removeError;

      // If user was the only member, soft-delete the team
      if (currentTeamMemberCount <= 1) {
        await supabase
          .from("challenge_teams")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", currentTeamId);
      }

      // Join new team
      const { error: joinError } = await supabase
        .from("challenge_team_members")
        .insert({
          team_id: team.id,
          challenge_id: challengeId,
          user_id: user.id,
          role: "member",
        });

      if (joinError) throw joinError;

      toast.success(`Switched to ${team.name}`);
      onOpenChange(false);
      setConfirmTeam(null);
      onTeamSwitched?.();
    } catch (error: any) {
      console.error("Error switching teams:", error);
      toast.error(error.message || "Failed to switch teams");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <ArrowRightLeft className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle>Switch Team</DialogTitle>
              </div>
            </div>
            <DialogDescription>
              Join a different team for this challenge. You'll leave your current team.
            </DialogDescription>
          </DialogHeader>

          {currentUserIsLead && currentTeamMemberCount > 1 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-500">You're the team lead</p>
                <p className="text-muted-foreground">
                  Consider transferring leadership before switching, or your team will need a new lead.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3 py-2 max-h-[300px] overflow-y-auto">
            {loading ? (
              <>
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </>
            ) : teams.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No other teams available for this challenge</p>
              </div>
            ) : (
              teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{team.name}</p>
                      <Badge variant="secondary" className="shrink-0">
                        {team.member_count} member{team.member_count !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                    {team.lead_name && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Crown className="h-3 w-3" />
                        <span>Led by {team.lead_name}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSwitchTeam(team)}
                    disabled={switching}
                  >
                    Join
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTeam} onOpenChange={() => setConfirmTeam(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave as Team Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              You're the lead of your current team with {currentTeamMemberCount - 1} other member(s). 
              If you leave, your team will need to choose a new lead. Are you sure you want to switch to "{confirmTeam?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTeam && performSwitch(confirmTeam)}
              disabled={switching}
            >
              {switching ? "Switching..." : "Switch Team"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SwitchTeamDialog;
